import type { ResumeData, TemplateId } from "@/types/resume";
import { Mail, Phone, MapPin, Globe, Linkedin, Github } from "lucide-react";
import { TemplateModernProfessional } from "./templates/TemplateModernProfessional";
import { TemplateMinimalExecutive } from "./templates/TemplateMinimalExecutive";
import { TemplateTechnicalClean } from "./templates/TemplateTechnicalClean";
import { TemplateTwoColumn } from "./templates/TemplateTwoColumn";
import { TemplateBase } from "./templates/TemplateBase";
import { BETTERCV_THEMES } from "./templates/bettercvThemes";
import { formatDate, getOrderedSections } from "./templates/templateUtils";
import { bodyFont, headingFont } from "./templates/useResumeFonts";

interface ResumePreviewProps {
  data: ResumeData;
  template: TemplateId;
  themeColor?: string;
}

export function ResumePreview({ data, template, themeColor }: ResumePreviewProps) {
  const color = themeColor || data.themeColor;
  switch (template) {
    case "modern-professional":
      return <TemplateModernProfessional data={data} overridePrimary={color} />;
    case "minimal-executive":
      return <TemplateMinimalExecutive data={data} overridePrimary={color} />;
    case "technical-clean":
      return <TemplateTechnicalClean data={data} overridePrimary={color} />;
    case "elegant-two-column":
      return <TemplateTwoColumn data={data} overridePrimary={color} />;
    case "ats-classic":
      return <TemplateATSClassic data={data} />;
    default: {
      const theme = BETTERCV_THEMES[template as string];
      if (theme) return <TemplateBase data={data} theme={theme} overridePrimary={color} />;
      return <TemplateATSClassic data={data} />;
    }
  }
}

function TemplateATSClassic({ data }: { data: ResumeData }) {
  const { personalInfo: p } = data;
  const hasContact = p.email || p.phone || p.city;
  const hasLinks = p.linkedin || p.portfolio || p.github;
  const FONT_BODY = bodyFont(data, "'Segoe UI', system-ui, sans-serif");
  const FONT_HEADING = headingFont(data, "Georgia, serif");

  const sectionRenderers: Record<string, React.ReactNode> = {
    summary: data.summary ? <Section key="summary" title="Professional Summary"><p>{data.summary}</p></Section> : null,
    workExperience: data.workExperience.length > 0 ? (
      <Section key="workExperience" title="Work Experience">
        {data.workExperience.map((exp) => (
          <div key={exp.id} className="mb-3 last:mb-0">
            <div className="flex items-start justify-between">
              <div><strong>{exp.jobTitle}</strong>{exp.company && <span> at {exp.company}</span>}</div>
              <span className="text-[10px] whitespace-nowrap" style={{ color: "#475569" }}>{exp.startDate && formatDate(exp.startDate)} — {exp.currentlyWorking ? "Present" : exp.endDate ? formatDate(exp.endDate) : ""}</span>
            </div>
            {exp.location && <p className="text-[10px]" style={{ color: "#475569" }}>{exp.location}</p>}
            {exp.responsibilities.filter(Boolean).length > 0 && <ul className="list-disc ml-4 mt-1 space-y-0.5">{exp.responsibilities.filter(Boolean).map((r, i) => <li key={i}>{r}</li>)}</ul>}
          </div>
        ))}
      </Section>
    ) : null,
    education: data.education.length > 0 ? (
      <Section key="education" title="Education">
        {data.education.map((edu) => (
          <div key={edu.id} className="mb-2 last:mb-0">
            <div className="flex items-start justify-between">
              <div><strong>{edu.degree}</strong>{edu.institution && <span> — {edu.institution}</span>}</div>
              {edu.graduationDate && <span className="text-[10px]" style={{ color: "#475569" }}>{formatDate(edu.graduationDate)}</span>}
            </div>
            {edu.location && <p className="text-[10px]" style={{ color: "#475569" }}>{edu.location}</p>}
            {edu.gpa && <p>GPA: {edu.gpa}</p>}
            {edu.honors && <p>{edu.honors}</p>}
          </div>
        ))}
      </Section>
    ) : null,
    skills: data.skills.length > 0 ? <Section key="skills" title="Skills">{data.skills.map((g) => <div key={g.id} className="mb-1.5 last:mb-0">{g.category && <strong>{g.category}: </strong>}<span>{g.skills.filter(Boolean).join(", ")}</span></div>)}</Section> : null,
    projects: data.projects.length > 0 ? <Section key="projects" title="Projects">{data.projects.map((p) => <div key={p.id} className="mb-2 last:mb-0"><strong>{p.title}</strong>{p.role && <span> — {p.role}</span>}{p.technologies.filter(Boolean).length > 0 && <p className="text-[10px]" style={{ color: "#475569" }}>Tech: {p.technologies.join(", ")}</p>}{p.description && <p className="mt-0.5">{p.description}</p>}</div>)}</Section> : null,
    certifications: data.certifications.length > 0 ? <Section key="certifications" title="Certifications">{data.certifications.map((c) => <div key={c.id} className="mb-1 last:mb-0"><strong>{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: "#1d4ed8" }}>{c.name}</a> : c.name}</strong>{c.issuer && <span> — {c.issuer}</span>}{c.issueDate && <span className="text-[10px] ml-2" style={{ color: "#475569" }}>{formatDate(c.issueDate)}</span>}</div>)}</Section> : null,
    languages: data.languages.length > 0 ? <Section key="languages" title="Languages"><p>{data.languages.map((l) => `${l.language} (${l.proficiency})`).join(", ")}</p></Section> : null,
    achievements: data.achievements.filter(Boolean).length > 0 ? <Section key="achievements" title="Achievements"><ul className="list-disc ml-4 space-y-0.5">{data.achievements.filter(Boolean).map((a, i) => <li key={i}>{a}</li>)}</ul></Section> : null,
  };

  return (
    <div className="p-6 text-xs leading-relaxed" style={{ fontFamily: FONT_BODY, color: "#1a1a2e", background: "#ffffff" }}>
      <div className="text-center mb-4">
        {p.fullName && <h1 className="text-lg font-bold mb-0.5" style={{ fontFamily: FONT_HEADING }}>{p.fullName}</h1>}
        {p.professionalTitle && <p className="text-sm" style={{ color: "#4a5568" }}>{p.professionalTitle}</p>}
        {(hasContact || hasLinks) && (
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2 text-[10px]" style={{ color: "#475569" }}>
            {p.email && <span className="flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" />{p.email}</span>}
            {p.phone && <span className="flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{p.phone}</span>}
            {p.city && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{p.city}{p.country ? `, ${p.country}` : ""}</span>}
            {p.linkedin && <span className="flex items-center gap-0.5"><Linkedin className="w-2.5 h-2.5" />{p.linkedin}</span>}
            {p.github && <span className="flex items-center gap-0.5"><Github className="w-2.5 h-2.5" />{p.github}</span>}
            {p.portfolio && <span className="flex items-center gap-0.5"><Globe className="w-2.5 h-2.5" />{p.portfolio}</span>}
          </div>
        )}
      </div>
      {getOrderedSections(data).map((key) => sectionRenderers[key])}
      {!p.fullName && !data.summary && data.workExperience.length === 0 && (
        <div className="text-center py-8" style={{ color: "#a0aec0" }}>
          <p className="text-sm">Start filling in your details to see the preview</p>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <h2 className="text-xs font-bold uppercase tracking-wider mb-1.5 pb-1 border-b" style={{ color: "#2d3748", borderColor: "#e2e8f0" }}>{title}</h2>
      {children}
    </div>
  );
}
